
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from '@/axios';
import { useAuthStore } from './authStore';

export const useAssessmentStore = defineStore('assessment', () => {

    const currentAssessment = ref(null);
    const assessmentLoading = ref(false);
    const assessmentError = ref(null);

    const clausesResponses = ref([]);
    const controlsResponses = ref([]);
    const currentStep = ref('clauses');

    const tempClausesAnswers = ref({});
    const tempControlsAnswers = ref({});
    const currentClauseIndex = ref(0);
    const currentControlIndexes = ref({});

    const aiAnalysis = ref(null);
    const aiAnalysisLoading = ref(false);
    const aiAnalysisError = ref(null);

    const hasActiveAssessment = computed(() => {
        return currentAssessment.value !== null;
    });

    const hasAIAnalysis = computed(() => {
        return aiAnalysis.value !== null && aiAnalysis.value !== undefined;
    });

    const loadAIAnalysis = async (assessmentId) => {

        aiAnalysisLoading.value = true;
        aiAnalysisError.value = null;

        try {

            if (aiAnalysis.value && aiAnalysis.value.assessment_id === assessmentId) {
                return aiAnalysis.value;
            }

            const response = await axios.get(`/assessments/${assessmentId}/saved-ai-analysis`);

            if (response.data.success) {

                if (response.data.data.exists && response.data.data.analysis) {

                    aiAnalysis.value = {
                        ...response.data.data.analysis,
                        generated_at: response.data.data.generated_at,
                        assessment_id: assessmentId
                    };

                    return aiAnalysis.value;

                } else {

                    return null;
                }

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            aiAnalysisError.value = error.response?.data?.message || 'Error cargando análisis de IA';
            return null;

        } finally {

            aiAnalysisLoading.value = false;

        }
    };

    const generateAIAnalysis = async (assessmentId) => {

        aiAnalysisLoading.value = true;
        aiAnalysisError.value = null;

        try {

            const response = await axios.get(`/assessments/${assessmentId}/ai-analysis`);

            if (response.data.success) {

                aiAnalysis.value = {
                    ...response.data.data,
                    generated_at: new Date().toISOString(),
                    assessment_id: assessmentId
                };

                return aiAnalysis.value;

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            aiAnalysisError.value = error.response?.data?.message || error.message || 'Error al generar el análisis de IA';
            console.error('Error generating AI analysis:', error);
            throw error;

        } finally {

            aiAnalysisLoading.value = false;

        }
    };

    const checkAndGenerateAIAnalysis = async (assessmentId) => {

        try {

            if (aiAnalysis.value && aiAnalysis.value.assessment_id === assessmentId) {
                return aiAnalysis.value;
            }

            const existingAnalysis = await loadAIAnalysis(assessmentId);

            if (!existingAnalysis) {

                const newAnalysis = await generateAIAnalysis(assessmentId);
                return newAnalysis;

            }

            return existingAnalysis;

        } catch (error) {

            console.error('Error en checkAndGenerateAIAnalysis:', error);
            return null;

        }
    };

    const totalProgress = computed(() => {

        if (!currentAssessment.value) return 0;

        const expectedClausesQuestions = 6;
        const expectedControlsQuestions = 27;

        const actualClausesAnswers = Object.keys(tempClausesAnswers.value).length;
        const actualControlsAnswers = Object.keys(tempControlsAnswers.value).length;

        const clausesPercentage = (actualClausesAnswers / expectedClausesQuestions) * 50;
        const controlsPercentage = (actualControlsAnswers / expectedControlsQuestions) * 50;

        const total = Math.min(clausesPercentage + controlsPercentage, 100);

        return Math.round(total);

    });

    const setTempClausesAnswers = (answers) => {
        tempClausesAnswers.value = { ...answers };
    };

    const setTempControlsAnswers = (answers) => {
        tempControlsAnswers.value = { ...answers };
    };

    const updateTempClauseAnswer = (questionId, answer) => {
        tempClausesAnswers.value[questionId] = answer;
    };

    const updateTempControlAnswer = (questionId, answer) => {
        tempControlsAnswers.value[questionId] = answer;
    };

    const setCurrentClauseIndex = (index) => {
        currentClauseIndex.value = index;
    };

    const setCurrentControlIndex = (domainId, index) => {
        currentControlIndexes.value[domainId] = index;
    };

    const getCurrentControlIndex = (domainId) => {
        return currentControlIndexes.value[domainId] || 0;
    };

    const clearTempData = () => {
        tempClausesAnswers.value = {};
        tempControlsAnswers.value = {};
        currentClauseIndex.value = 0;
        currentControlIndexes.value = {};
    };

    const findControlInDomains = (domainsData, controlId) => {

        for (const domain of domainsData) {

            if (domain.controls && Array.isArray(domain.controls)) {

                const foundControl = domain.controls.find(control => {
                    return control.id == controlId;
                });

                if (foundControl) {
                    return foundControl;
                }

            }
        }

        return null;

    };

    const loadTempClausesAnswers = () => {

        try {

            const newTempAnswers = {};

            clausesResponses.value.forEach(response => {

                if (response.question_id && response.answer) {

                    newTempAnswers[response.question_id] = {
                        id: response.answer.id,
                        answer: response.answer.answer,
                        maturity_level_id: response.maturity_level_id
                    };

                }
            });

            tempClausesAnswers.value = {
                ...tempClausesAnswers.value,
                ...newTempAnswers
            };


        } catch (error) {

            console.error('Error cargando temp clauses answers:', error);

        }
    };

    const loadTempControlsAnswers = (domainsData = []) => {

        try {

            if (domainsData && domainsData.length > 0) {

                const newTempAnswers = {};

                controlsResponses.value.forEach(response => {

                    if (response.control_id && response.answer) {

                        const questionId = findQuestionIdByControlId(response.control_id, domainsData);

                        if (questionId) {

                            newTempAnswers[questionId] = {
                                id: response.answer.id,
                                answer: response.answer.answer,
                                maturity_level_id: response.maturity_level_id
                            };

                        }

                    }
                });

                tempControlsAnswers.value = {
                    ...tempControlsAnswers.value,
                    ...newTempAnswers
                };
            }

        } catch (error) {

            console.error('Error cargando temp controls answers:', error);

        }
    };

    const loadTempAnswersFromResponses = (domainsData = []) => {

        loadTempClausesAnswers();
        loadTempControlsAnswers(domainsData);

    };

    const findQuestionIdByControlId = (controlId, domainsData) => {

        if (!domainsData || !Array.isArray(domainsData)) {
            return null;
        }

        for (const domain of domainsData) {

            if (domain.controls && Array.isArray(domain.controls)) {

                const control = domain.controls.find(c => c.id == controlId);

                if (control && control.subcategories) {

                    for (const subcategory of control.subcategories) {

                        if (subcategory.questions && subcategory.questions.length > 0) {

                            const questionId = subcategory.questions[0].id;
                            return questionId;
                        }

                    }
                }
            }
        }

        return null;
    };

    const checkLastAssessment = async () => {

        const authStore = useAuthStore();

        try {

            const response = await axios.get('/last-user-assessment', {
                params: {
                    user_id: authStore.id
                }
            });

            return response.data.success ? response.data.data : null;

        } catch (error) {

            console.error('Error verificando último assessment:', error);
            return null;

        }
    };

    const initializeAssessment = async (useLastAssessment = false) => {

        const authStore = useAuthStore();
        assessmentLoading.value = true;
        assessmentError.value = null;
        aiAnalysis.value = null;
        aiAnalysisError.value = null;

        try {

            if (hasActiveAssessment.value && !useLastAssessment) {
                return currentAssessment.value;
            }

            const assessmentData = {

                purpose: { id: 3, purpose: 'treasury' },
                assessment_date: new Date().toISOString().split('T')[0],
                status: { id: 1, status: 'Activo' },
                overall_maturity: 0,
                phva_score: 0,
                nist_score: 0,
                controls_score: 0,
                user_id: authStore.id,
                use_last_assessment: useLastAssessment

            };

            const response = await axios.post('/assessments', assessmentData);

            if (response.data.success) {

                currentAssessment.value = response.data.data;

                if (useLastAssessment && currentAssessment.value.id) {
                    await fetchAssessmentResults(currentAssessment.value.id);
                }

                return currentAssessment.value;

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            assessmentError.value = error.message;
            throw error;

        } finally {

            assessmentLoading.value = false;

        }

    };

    const saveClausesResponses = async (responses) => {

        try {

            if (!hasActiveAssessment.value) {
                throw new Error('No hay un assessment activo');
            }

            const responsesWithAssessmentInfo = responses.map(response => ({
                ...response,
                assessment_id: currentAssessment.value.id,
                justification: response.justification || '',
                gap: response.gap || '',
                is_applicable: response.is_applicable !== undefined ? response.is_applicable : true
            }));

            const response = await axios.post('/assessment-clauses/bulk', {
                responses: responsesWithAssessmentInfo
            });

            if (response.data.success) {

                clausesResponses.value = [...clausesResponses.value, ...responses];
                currentStep.value = 'controls';

                return response.data;

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            console.error('Error guardando respuestas de cláusulas:', error);
            throw error;

        }
    };

    const saveControlsResponses = async (responses) => {

        try {

            if (!hasActiveAssessment.value) {
                throw new Error('No hay un assessment activo');
            }

            const responsesWithAssessmentInfo = responses.map(response => ({
                ...response,
                assessment_id: currentAssessment.value.id,
                justification: response.justification || '',
                gap: response.gap || '',
                is_applicable: response.is_applicable !== undefined ? response.is_applicable : true
            }));

            const response = await axios.post('/assessment-controls/bulk', {
                responses: responsesWithAssessmentInfo
            });

            if (response.data.success) {

                controlsResponses.value = [...controlsResponses.value, ...responses];
                currentStep.value = 'results';

                return response.data;

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            console.error('Error guardando respuestas de controles:', error);
            throw error;

        }
    };

    const fetchAssessmentResults = async (assessmentId) => {

        assessmentLoading.value = true;
        assessmentError.value = null;

        try {

            const response = await axios.get(`/assessments/${assessmentId}/results`);

            if (response.data.success) {

                const assessmentData = response.data.data;

                currentAssessment.value = {
                    id: assessmentData.id,
                    purpose: assessmentData.purpose,
                    assessment_date: assessmentData.assessment_date,
                    user_id: assessmentData.user_id,
                    status: assessmentData.status,
                    overall_maturity: assessmentData.overall_maturity,
                    phva_score: assessmentData.phva_score,
                    nist_score: assessmentData.nist_score,
                    controls_score: assessmentData.controls_score,
                    created_at: assessmentData.created_at,
                    updated_at: assessmentData.updated_at
                };

                clausesResponses.value = assessmentData.assessment_clauses || [];
                controlsResponses.value = assessmentData.assessment_controls || [];

                return response.data;

            } else {

                throw new Error(response.data.message || 'Error fetching assessment results');

            }

        } catch (error) {

            assessmentError.value = error.message;
            throw error;

        } finally {

            assessmentLoading.value = false;

        }
    };

    const completeAssessment = async () => {

        try {

            if (!hasActiveAssessment.value) {
                throw new Error('No hay un assessment activo');
            }

            const response = await axios.put(`/assessments/${currentAssessment.value.id}`, {

                status: {
                    state: 'completed',
                    progress: 100,
                    current_step: 'completed'
                },

                completed_at: new Date().toISOString()

            });

            if (response.data.success) {

                currentAssessment.value = response.data.data;
                return response.data;

            } else {

                throw new Error(response.data.message || 'Error en la respuesta del servidor');

            }

        } catch (error) {

            console.error('Error completando assessment:', error);
            throw error;

        }
    };

    const resetAssessment = () => {

        currentAssessment.value = null;
        clausesResponses.value = [];
        controlsResponses.value = [];
        currentStep.value = 'clauses';
        assessmentError.value = null;
        aiAnalysisError.value = null;
        aiAnalysis.value = null;

        clearTempData();

    };

    const continueAssessment = (assessmentData) => {

        currentAssessment.value = assessmentData;

    };

    return {

        currentAssessment,
        assessmentLoading,
        assessmentError,
        clausesResponses,
        controlsResponses,
        currentStep,
        tempClausesAnswers,
        tempControlsAnswers,
        currentClauseIndex,
        currentControlIndexes,
        aiAnalysis,
        aiAnalysisLoading,
        aiAnalysisError,

        hasActiveAssessment,
        totalProgress,

        setTempClausesAnswers,
        setTempControlsAnswers,
        updateTempClauseAnswer,
        updateTempControlAnswer,
        setCurrentClauseIndex,
        setCurrentControlIndex,
        getCurrentControlIndex,
        clearTempData,

        initializeAssessment,
        checkLastAssessment,
        saveClausesResponses,
        saveControlsResponses,
        completeAssessment,
        resetAssessment,
        continueAssessment,
        loadTempAnswersFromResponses,
        loadTempClausesAnswers,
        loadTempControlsAnswers,
        hasAIAnalysis,
        loadAIAnalysis,
        generateAIAnalysis,
        checkAndGenerateAIAnalysis,
        fetchAssessmentResults
    };
}, {
    persist: {
        storage: localStorage,
        key: 'mspi-assessment',
        paths: [
            'currentAssessment',
            'clausesResponses',
            'controlsResponses',
            'currentStep',
            'tempClausesAnswers',
            'tempControlsAnswers',
            'currentClauseIndex',
            'currentControlIndexes',
            'aiAnalysis'
        ]
    }
});